import { Link } from '@/components/navigation/Link';
import { MonabidLogo } from '@/components/monabid-logo';
import type { ReactNode } from "react";
import { prefixes, useTypedTranslation } from "@/locales";
import { PageContainer } from "@/components/layout/PageContainer.tsx";
import { Facebook, Instagram, LinkedIn, Pinterest, X } from "@/components/_untitled/foundations/social-icons";
import { build } from '@/config/routes';

interface FooterLink {
    label: string;
    href: string;
}

interface FooterSection {
    title: string;
    links: FooterLink[];
}

interface SocialLink {
    name: string;
    href: string;
    icon: ReactNode;
}

export const Footer = () => {
    const [tFooter, tDiscover, tHelpCenter, tHowItWorks, tLegal, tMonabid] = useTypedTranslation(
        prefixes('footer', 'footer.discover', 'footer.helpCenter', 'footer.howItWorks', 'footer.legal', 'footer.monabid')
    );

    const footerSections: FooterSection[] = [
        {
            title: tHowItWorks('title'),
            links: [
                { label: tHowItWorks('howToBid'), href: build.pages('how-to-bid') },
                { label: tHowItWorks('howToSell'), href: build.pages('how-to-sell') },
                { label: tHowItWorks('faq'), href: build.pages('faq') },
            ],
        },
        {
            title: tMonabid('title'),
            links: [
                { label: tMonabid('aboutUs'), href: build.pages('about-us') },
                { label: tMonabid('sellWithUs'), href: build.pages('sell-with-us') },
                { label: tMonabid('careers'), href: build.pages('careers') },
            ],
        },
        {
            title: tHelpCenter('title'),
            links: [
                { label: tHelpCenter('support'), href: build.pages('support') },
                { label: tHelpCenter('contactUs'), href: build.pages('contact-us') },
            ],
        },
        {
            title: tDiscover('title'),
            links: [
                { label: tDiscover('consignItem'), href: build.pages('consign') },
                { label: tDiscover('allAuctions'), href: build.pages('all-auctions') },
            ],
        },
    ];

    const legalLinks: FooterLink[] = [
        { label: tLegal('termsAndConditions'), href: build.pages('terms-and-conditions') },
        { label: tLegal('intellectualProperty'), href: build.pages('intellectual-property') },
        { label: tLegal('cookiePolicy'), href: build.pages('cookie-policy') },
        { label: tLegal('accessibility'), href: build.pages('accessibility') },
        { label: tLegal('privacyPolicy'), href: build.pages('privacy-policy') },
    ];

    const socialLinks: SocialLink[] = [
        {
            name: 'X',
            href: 'https://x.com/themonabid',
            icon: <X size={ 18 }/>,
        },
        {
            name: 'LinkedIn',
            href: 'https://linkedin.com/company/themonabid',
            icon: <LinkedIn size={ 18 }/>,
        },
        {
            name: 'Facebook',
            href: 'https://facebook.com/themonabid',
            icon: <Facebook size={ 18 }/>,
        },
        {
            name: 'Instagram',
            href: 'https://instagram.com/themonabid',
            icon: <Instagram size={ 18 }/>,
        },
        {
            name: 'Pinterest',
            href: 'https://pinterest.com/monabid',
            icon: <Pinterest/>,
        },
    ];

    return (
        <footer className="w-full bg-black text-white">
            {/* Main Section */ }
            <PageContainer className="py-12">
                {/* Logo and Tagline */ }
                <div className="mb-12">
                    <MonabidLogo color="white"/>
                    <p className="text-sm text-gray-400 mt-2 text-start">
                        { tFooter('tagline') }
                    </p>
                </div>

                {/* Link Columns */ }
                <nav className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-12"
                     aria-label={ tFooter('navigation') }>
                    { footerSections.map((section) => (
                        <div key={ section.title }>
                            <h3 className="text-sm font-semibold uppercase mb-4 text-start">{ section.title }</h3>
                            <ul className="space-y-3">
                                { section.links.map((link) => (
                                    <li key={ link.href }>
                                        <Link
                                            href={ link.href }
                                            className="text-sm text-start text-gray-400 hover:text-white transition-colors block"
                                        >
                                            { link.label }
                                        </Link>
                                    </li>
                                )) }
                            </ul>
                        </div>
                    )) }
                </nav>
            </PageContainer>

            {/* Bottom Bar */ }
            <div className="border-t border-gray-800">
                <PageContainer className="py-6">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        {/* Copyright */ }
                        <p className="text-xs text-gray-500 text-start">
                            { tFooter('copyright', { year: new Date().getFullYear() }) }
                        </p>

                        {/* Legal Links */ }
                        <nav className="flex flex-wrap justify-center items-center" aria-label="Legal links">
                            { legalLinks.map((link) => (
                                <Link
                                    key={ link.href }
                                    href={ link.href }
                                    className="text-xs text-gray-500 hover:text-gray-300 transition-colors after:content-['•'] after:text-gray-700 after:mx-2 last:after:content-none"
                                >
                                    { link.label }
                                </Link>
                            )) }
                        </nav>

                        {/* Social Icons */ }
                        <ul className="flex items-center gap-4" aria-label={ tFooter('socialMedia') }>
                            { socialLinks.map((social) => (
                                <li key={ social.name }>
                                    <Link
                                        external={ social.href }
                                        className="text-gray-500 hover:text-white transition-colors"
                                        aria-label={ social.name }
                                    >
                                        { social.icon }
                                    </Link>
                                </li>
                            )) }
                        </ul>
                    </div>
                </PageContainer>
            </div>
        </footer>
    );
};
